import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFeaturedMovies, getActorsByMovie } from "@/utils/movie";

export default function HeroCarousel() {
  const slides = getFeaturedMovies().slice(0, 4);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  if (!slides.length) return null;
  const active = slides[index];
  const cast = getActorsByMovie(active).map((a: any) => a.name).slice(0, 3).join(" / ");

  const go = (dir: number) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  };

  return (
    <section
      className="relative w-full h-[560px] md:h-[720px] overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.banner}
            alt={`${s.title} 焦点剧照`}
            title={`${s.title} 剧情介绍与演员表`}
            className={`w-full h-full object-cover object-top ${i === index ? "animate-kenburns" : ""}`}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/40"></div>

      <div className="relative h-full max-w-[1280px] mx-auto px-4 md:px-8 flex items-center">
        <div key={active.id} className="w-full max-w-2xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
              焦点推荐
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-50/15 text-background-50 border border-background-50/30">
              {active.type}
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-accent-400 font-medium">
              <i className="ri-star-fill"></i>
              {active.rating}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-[56px] font-bold text-background-50 leading-tight text-balance">
            {active.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100/90">
            <span>{active.year}年</span>
            <span className="w-1 h-1 rounded-full bg-background-100/50"></span>
            <span>{active.region}</span>
            <span className="w-1 h-1 rounded-full bg-background-100/50"></span>
            <span>{(active.genres || []).join(" · ")}</span>
            <span className="w-1 h-1 rounded-full bg-background-100/50"></span>
            <span>导演 {active.director}</span>
          </div>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-background-200/85 line-clamp-3 max-w-xl">
            {active.summary}
          </p>
          <p className="mt-2 text-xs text-background-200/70">主演：{cast}</p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              href={`/detail/${active.id}`}
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看剧情详情
            </Link>
            <Link
              href="/category"
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/10 text-background-50 border border-background-50/40 text-sm font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      {/* controls */}
      <div className="absolute bottom-6 md:bottom-10 left-0 right-0">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIndex(i)}
                aria-label={`切换到 ${s.title}`}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
                }`}
              ></button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => go(-1)}
              aria-label="上一部"
              className="w-10 h-10 rounded-full border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={() => go(1)}
              aria-label="下一部"
              className="w-10 h-10 rounded-full border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}